import React, { useState } from "react";
import { motion } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import api from "../lib/api";
import { INTERESTS, ROLES } from "../data/content";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, ArrowUpRight, Mail, Clock, MapPin } from "lucide-react";

const empty = { name: "", email: "", organization: "", role: ROLES[0], interest: INTERESTS[0], message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organization) {
      toast.error("Please fill in your name, email, and organization.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Request received — we'll be in touch shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-dark border-white/15 text-white h-12 rounded-none focus-visible:ring-teal focus-visible:ring-1 focus-visible:border-teal placeholder:text-slate-600";

  return (
    <div>
      <PageHero
        testid="contact-hero"
        overline="Contact · Book a Demo"
        title={<>Let's build<br />your system</>}
        subtitle="Tell us about your program. We'll follow up to schedule a 30-minute demo tailored to your data."
      />

      <section className="py-20 bg-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mb-8">
                Talk to a data engineer, not a call center.
              </h2>
              <div className="space-y-6">
                {[
                  { icon: Mail, label: "Email", value: "info@athleticdatasolutions.com" },
                  { icon: Clock, label: "Hours", value: "Mon–Fri · 9am – 6pm ET" },
                  { icon: MapPin, label: "Serving", value: "Associations & clubs nationwide" },
                ].map((c) => (
                  <div key={c.label} className="flex items-center gap-4">
                    <span className="grid place-items-center w-11 h-11 border border-white/15 text-teal">
                      <c.icon className="w-5 h-5" strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{c.label}</p>
                      <p className="text-white">{c.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {done ? (
                <motion.div
                  data-testid="contact-success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-dark-surface border border-teal/40 p-12 text-center flex flex-col items-center"
                >
                  <CheckCircle2 className="w-16 h-16 text-teal mb-6" strokeWidth={1.5} />
                  <h3 className="font-display text-3xl font-black uppercase tracking-tighter mb-3">Request received</h3>
                  <p className="text-slate-400 max-w-md mb-8">
                    Thanks for reaching out. A member of our team will contact you within one business day to schedule your demo.
                  </p>
                  <button
                    data-testid="contact-reset"
                    onClick={() => setDone(false)}
                    className="group inline-flex items-center gap-2 text-teal font-display font-bold uppercase text-sm tracking-wide"
                  >
                    Submit another request <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </motion.div>
              ) : (
                <form data-testid="contact-form" onSubmit={submit} className="bg-dark-surface border border-white/10 p-8 md:p-10 space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="font-mono text-xs uppercase tracking-widest text-slate-400">Full name *</Label>
                      <Input id="name" data-testid="input-name" className={inputCls} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Coach" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="font-mono text-xs uppercase tracking-widest text-slate-400">Email *</Label>
                      <Input id="email" type="email" data-testid="input-email" className={inputCls} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="jane@school.edu" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="org" className="font-mono text-xs uppercase tracking-widest text-slate-400">Organization *</Label>
                    <Input id="org" data-testid="input-organization" className={inputCls} value={form.organization} onChange={(e) => update("organization", e.target.value)} placeholder="Lincoln High Athletics" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="font-mono text-xs uppercase tracking-widest text-slate-400">Your role</Label>
                      <Select value={form.role} onValueChange={(v) => update("role", v)}>
                        <SelectTrigger data-testid="select-role" className={inputCls}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-dark-surface border-white/15 text-white rounded-none">
                          {ROLES.map((r) => <SelectItem key={r} value={r} className="focus:bg-teal/20 focus:text-white">{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-mono text-xs uppercase tracking-widest text-slate-400">Interested in</Label>
                      <Select value={form.interest} onValueChange={(v) => update("interest", v)}>
                        <SelectTrigger data-testid="select-interest" className={inputCls}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-dark-surface border-white/15 text-white rounded-none">
                          {INTERESTS.map((r) => <SelectItem key={r} value={r} className="focus:bg-teal/20 focus:text-white">{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-mono text-xs uppercase tracking-widest text-slate-400">Tell us about your needs</Label>
                    <Textarea id="message" data-testid="input-message" rows={4} className={`${inputCls} h-auto py-3`} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="We have 40 years of track records to digitize…" />
                  </div>

                  <motion.button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={loading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="group w-full flex items-center justify-center gap-2 bg-orange text-white px-7 py-4 font-display font-bold uppercase tracking-wide hover:bg-orange-hover transition-colors duration-300 disabled:opacity-60"
                  >
                    {loading ? "Sending…" : "Book my demo"}
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </motion.button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
